/**
 * Session feature (Preact port).
 *
 * Provides the landing page (new session) and per-session conversation page,
 * real-time session list via SSE, per-session event streaming with markdown
 * rendering, sidebar session entries with status indicators, and create/send/
 * archive actions.
 */
import { useState, useEffect, useRef, useCallback } from 'preact/hooks';
import { apiFetch, loadEarlierMessages, markRead, fsContentURL } from '../lib/api.js';
import { useDialog } from '../components/Dialog.jsx';
import { Compose } from '../components/Compose.jsx';
import { Markdown } from '../components/Markdown.jsx';
import { Icon } from '../components/Icons.jsx';
import { loadCache, saveCache, MAX_CACHED } from '../lib/cache.js';
import { fetchSSE } from '../lib/sse.js';
import { setOpenSession } from '../lib/share.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const partData = (msg, type) => {
  const p = (msg.parts || []).find((x) => x.type === type);
  return p ? p.data : '';
};

const fsIDFrom = (data) =>
  typeof data === 'string' && data.startsWith('fs://')
    ? data.slice('fs://'.length)
    : '';

const formatSize = (bytes) => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${bytes} B`;
};

// ---------------------------------------------------------------------------
// Session-list SSE
// ---------------------------------------------------------------------------

/**
 * useSessionStore maintains the session map and sidebar entries, driven by the
 * /api/s SSE stream.
 */
export function useSessionStore(sidebar) {
  const [sessions, setSessions] = useState(new Map());
  const { open } = useDialog();
  const connectingRef = useRef(null);

  const showConnecting = useCallback(() => {
    if (connectingRef.current) return;
    connectingRef.current = open({
      title: 'Connecting',
      body: 'Reconnecting to the server…',
      modal: true,
    });
  }, [open]);

  const hideConnecting = useCallback(() => {
    if (connectingRef.current) {
      connectingRef.current.close();
      connectingRef.current = null;
    }
  }, []);

  const archiveSession = useCallback(
    async (id) => {
      try {
        await apiFetch(`/api/s/${id}`, { method: 'DELETE' });
      } catch {
        /* ignore */
      }
      sidebar.deleteEntry('sessions', id);
      setSessions((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [sidebar]
  );

  const addEntry = useCallback(
    (sess) => {
      sidebar.addEntry('sessions', {
        id: sess.id,
        label: sess.title,
        route: `/s/${sess.id}`,
        running: sess.state === 'running',
        unread: sess.unread,
        contextMenu: [
          { id: 'archive', label: 'Archive', onClick: () => archiveSession(sess.id) },
        ],
      });
    },
    [sidebar, archiveSession]
  );

  useEffect(() => {
    let sse;
    let closed = false;
    let reconnectTimer = null;

    const parseID = (raw) => String(raw).replace(/^"|"$/g, '');

    const start = () => {
      if (closed) return;
      sse?.close();

      sse = fetchSSE('/api/s', {
        onOpen() {
          hideConnecting();
        },
        onError() {
          showConnecting();
          reconnectTimer = setTimeout(start, 2000);
        },
        onEvent(event, data) {
          hideConnecting();
          if (event === 'S') {
            const sess = JSON.parse(data);
            setSessions((prev) => {
              const exists = prev.has(sess.id);
              if (!exists) addEntry(sess);
              else sidebar.updateEntry('sessions', sess.id, { label: sess.title, running: sess.state === 'running', unread: sess.unread });
              return new Map(prev).set(sess.id, sess);
            });
          } else if (event === 'B') {
            const id = parseID(data);
            setSessions((prev) => {
              const next = new Map(prev);
              const s = next.get(id);
              if (s) next.set(id, { ...s, state: 'running' });
              sidebar.updateEntry('sessions', id, { running: true });
              return next;
            });
          } else if (event === 'E') {
            const id = parseID(data);
            setSessions((prev) => {
              const next = new Map(prev);
              const s = next.get(id);
              if (s && s.state === 'running') next.set(id, { ...s, state: 'idle' });
              sidebar.updateEntry('sessions', id, { running: false });
              return next;
            });
          } else if (event === 'A' || event === 'U' || event === 'X') {
            const id = parseID(data);
            sidebar.deleteEntry('sessions', id);
            setSessions((prev) => {
              const next = new Map(prev);
              next.delete(id);
              return next;
            });
          }
        },
      });
    };

    start();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      sse?.close();
    };
  }, []);

  return { sessions, showConnecting, hideConnecting, archiveSession };
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

export async function createSession(title, message, files = []) {
  try {
    const resp = await apiFetch('/api/s', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: title || 'New session', message, files }),
    });
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
}

export async function sendMessage(sessionID, text, files = []) {
  try {
    await apiFetch(`/api/s/${sessionID}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: text, files }),
    });
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Page components
// ---------------------------------------------------------------------------

export function LandingPage({ sessions, addSessionEntry }) {
  const startSession = async (text, files) => {
    const sess = await createSession(text, text, files || []);
    if (!sess) return;
    addSessionEntry(sess);
    window.location.hash = '#/s/' + sess.id;
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="text-center max-w-xl w-full">
          <h1 className="text-3xl font-bold text-base-content mb-2">Talos</h1>
          <p className="text-base text-base-content/60 mb-8">
            Your agentic AI assistant. Ask anything, analyze errors, or share a repository.
          </p>
          <Compose placeholder="Ask anything..." autoFocus onSubmit={startSession} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bubbles
// ---------------------------------------------------------------------------

function UserBubble({ msg }) {
  return (
    <div className="self-end max-w-[85%]">
      <div className="event-content rounded-2xl px-4 py-2 text-sm text-white bg-primary">
        <UserParts parts={msg.parts || []} />
      </div>
    </div>
  );
}

function UserParts({ parts }) {
  return (
    <>
      {parts.map((p, i) => {
        if (p.type === 'image') {
          const fsPath = fsIDFrom(p.data);
          return (
            <img
              key={i}
              src={fsContentURL(fsPath)}
              className="max-h-48 max-w-full rounded-lg mt-1 object-contain"
              alt={p.name || 'image'}
            />
          );
        }
        if (p.type === 'file') {
          const fsPath = fsIDFrom(p.data);
          return (
            <a
              key={i}
              href={fsContentURL(fsPath)}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 text-xs underline max-w-full truncate"
            >
              📎 {p.name || 'attachment'}
            </a>
          );
        }
        return <span key={i}>{p.data}</span>;
      })}
    </>
  );
}

function AssistantBubble({ msg }) {
  return (
    <div className="w-full max-w-3xl mx-auto">
      <Markdown className="event-content prose prose-sm max-w-none" text={(msg.parts || []).map((p) => p.data).join('')} />
    </div>
  );
}

function ToolView({ msg }) {
  const role = msg.role;
  const rows = [];
  if (role === 'tool_req') {
    const argsRaw = partData(msg, 'tool_args');
    let argsDisplay = argsRaw;
    if (argsRaw) {
      try {
        argsDisplay = JSON.stringify(JSON.parse(argsRaw), null, 2);
      } catch {
        /* keep raw */
      }
    }
    rows.push({ label: 'Call ID', value: partData(msg, 'tool_id') });
    rows.push({ label: 'Name', value: partData(msg, 'text') });
    rows.push({ label: 'Parameters', value: argsDisplay, pre: true });
  } else if (role === 'tool_rsp') {
    rows.push({ label: 'Call ID', value: partData(msg, 'tool_id') });
    rows.push({ label: 'Result', value: partData(msg, 'text'), pre: true });
  }
  return (
    <div className="rounded-lg border border-base-300 bg-base-100 overflow-x-auto">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <th className="text-left font-medium text-base-content/60 whitespace-nowrap pr-3 align-top px-3 py-1.5">
                {r.label}
              </th>
              <td className="text-base-content break-words px-3 py-1.5">
                {r.pre ? (
                  <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug m-0">
                    {r.value || ' '}
                  </pre>
                ) : (
                  <span>{r.value || ' '}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ReasoningView renders a reasoning trace's markdown content. It is only shown
// inside the thinking bubble, where any non-bubbled content is reasoning, so no
// "Reasoning" label or icon is needed.
function ReasoningView({ msg }) {
  return (
    <Markdown className="text-sm prose prose-sm max-w-none text-base-content/60" text={(msg.parts || []).map((p) => p.data).join('')} />
  );
}

function ErrorBubble({ msg }) {
  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="event-content inline-block rounded-xl bg-error/15 text-error text-sm px-4 py-2">
        {(msg.parts || []).map((p) => p.data).join('')}
      </div>
    </div>
  );
}

function CommandBubble({ msg }) {
  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="event-content rounded-xl bg-base-200/70 border border-base-300 px-4 py-3">
        <Markdown className="prose prose-sm max-w-none" text={(msg.parts || []).map((p) => p.data).join('')} />
      </div>
    </div>
  );
}

// ThinkingItem renders a single message inside the thinking super bubble:
// reasoning traces and tool request/response rows.
function ThinkingItem({ msg }) {
  if (msg.role === 'reasoning') return <ReasoningView msg={msg} />;
  return <ToolView msg={msg} />;
}

// ThinkingStreaming renders the in-flight message when it is a thinking role.
function ThinkingStreaming({ role, text }) {
  if (role === 'reasoning') {
    return (
      <Markdown className="text-sm prose prose-sm max-w-none text-base-content/60" text={text} />
    );
  }
  return (
    <div className="rounded-lg border border-base-300 bg-base-100 px-3 py-2">
      <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug m-0 text-base-content/70">{text}</pre>
    </div>
  );
}

// ThinkingBubble is the collapsed "super bubble" that wraps all the intermediate
// steps of a round (reasoning, tool request/response). It is collapsed by
// default; clicking the header expands the accumulated steps.
function ThinkingBubble({ msgs, streaming }) {
  const count = (msgs ? msgs.length : 0) + (streaming ? 1 : 0);
  return (
    <div className="w-full max-w-3xl mx-auto event-round-thinking flex flex-col gap-1">
      <details className="group">
        <summary className="flex items-center gap-1.5 cursor-pointer select-none list-none text-xs text-base-content/40 hover:text-base-content/60 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" class="w-3 h-3 shrink-0 transition-transform group-open:rotate-90" aria-hidden="true">
            <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clip-rule="evenodd" />
          </svg>
          <Icon name="lightbulb" className="w-3.5 h-3.5 shrink-0" />
          <span className="font-medium">Thinking</span>
          {count > 0 && <span className="text-base-content/30">({count})</span>}
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          {(msgs || []).map((msg) => (
            <ThinkingItem key={msg.id} msg={msg} />
          ))}
          {streaming && <ThinkingStreaming role={streaming.role} text={streaming.text} />}
        </div>
      </details>
    </div>
  );
}

function EventBubble({ msg }) {
  switch (msg.role) {
    case 'user':
    case 'command_req':
      return <UserBubble msg={msg} />;
    case 'assistant':
      return <AssistantBubble msg={msg} />;
    case 'error':
      return <ErrorBubble msg={msg} />;
    case 'command_rsp':
      return <CommandBubble msg={msg} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Session events SSE
// ---------------------------------------------------------------------------

/**
 * useSessionEvents connects to /api/s/{id}/events and maintains the ordered
 * list of messages, incrementally updating the in-progress (streaming) message.
 * Returns { messages, streaming, pendingMsgs, hasMore, loadEarlier } where
 * pendingMsgs are user messages that arrived while the session was running and
 * have not yet been processed, hasMore indicates whether older messages may
 * exist on the server, and loadEarlier fetches the next page.
 */
function useSessionEvents(sessionID) {
  const cache = loadCache(sessionID);

  const [messages, setMessages] = useState(cache.messages);
  const [streaming, setStreaming] = useState(null); // { id, role, text }
  const [pendingMsgs, setPendingMsgs] = useState(cache.pendingMessages);
  const [hasMore, setHasMore] = useState(cache.messages.length >= MAX_CACHED);
  const seenRef = useRef(new Set(cache.messages.map((m) => m.id)));
  const pendingRef = useRef(new Map(cache.pendingMessages.map((m) => [m.id, m])));

  // Persist messages to localStorage whenever they change.
  useEffect(() => {
    saveCache(sessionID, messages, pendingMsgs);
  }, [sessionID, messages, pendingMsgs]);

  useEffect(() => {
    let sse;
    let closed = false;
    seenRef.current = new Set(cache.messages.map((m) => m.id));
    pendingRef.current = new Map(cache.pendingMessages.map((m) => [m.id, m]));
    setMessages(cache.messages);
    setStreaming(null);
    setPendingMsgs(cache.pendingMessages);

    const start = () => {
      if (closed) return;
      sse?.close();

      // Resume from the last non-pending message in cache so we only
      // download events that arrived after the previous page load.
      const lastParam = cache.lastID ? `?last=${encodeURIComponent(cache.lastID)}` : '';

      sse = fetchSSE(`/api/s/${sessionID}/events${lastParam}`, {
        onError() {
          // Reconnect after a short delay.
          if (!closed) {
            setTimeout(start, 2000);
          }
        },
        onEvent(event, data) {
          switch (event) {
            case 'm': {
              try {
                const msg = JSON.parse(data);
                if (!msg || msg.role === 'system') return;
                const text = (msg.parts || []).map((p) => p.data).join('');
                setStreaming((prev) => ({
                  id: msg.id,
                  role: msg.role,
                  text,
                }));
              } catch {
                /* ignore */
              }
              break;
            }
            case 'd': {
              const delta = data || '';
              if (!delta) break;
              setStreaming((prev) =>
                prev ? { ...prev, text: prev.text + delta } : prev
              );
              break;
            }
            case 'P': {
              try {
                const msg = JSON.parse(data);
                if (!msg || msg.role === 'system') return;
                pendingRef.current.set(msg.id, msg);
                setPendingMsgs((prev) => {
                  const idx = prev.findIndex((m) => m.id === msg.id);
                  if (idx >= 0) {
                    const next = [...prev];
                    next[idx] = msg;
                    return next;
                  }
                  return [...prev, msg];
                });
              } catch {
                /* ignore */
              }
              break;
            }
            case 'M': {
              try {
                const msg = JSON.parse(data);
                if (!msg || msg.role === 'system') return;
                if (pendingRef.current.has(msg.id)) {
                  pendingRef.current.delete(msg.id);
                  setPendingMsgs((prev) => prev.filter((m) => m.id !== msg.id));
                }
                if (seenRef.current.has(msg.id)) return;
                seenRef.current.add(msg.id);
                setStreaming((prev) => (prev && prev.id === msg.id ? null : prev));
                setMessages((prev) => [...prev, { ...msg, parts: msg.parts || [] }]);
              } catch {
                /* ignore */
              }
              break;
            }
          }
        },
      });
    };

    start();
    return () => {
      closed = true;
      sse?.close();
    };
  }, [sessionID]);

  // loadEarlier fetches the next page of older messages from the server
  // and prepends them to the message list.
  const loadEarlier = useCallback(async () => {
    if (messages.length === 0) return;
    const firstID = messages[0].id;
    try {
      const older = await loadEarlierMessages(sessionID, firstID, 100);
      if (older.length < 100) setHasMore(false);
      setMessages((prev) => {
        const existing = new Set(prev.map((m) => m.id));
        const newMsgs = older.filter((m) => !existing.has(m.id));
        return [...newMsgs, ...prev];
      });
      for (const m of older) seenRef.current.add(m.id);
    } catch {
      /* silently ignore load failures */
    }
  }, [sessionID, messages]);

  return { messages, streaming, pendingMsgs, hasMore, loadEarlier };
}

// Roles that belong inside the collapsed "thinking" super bubble that wraps the
// intermediate steps of a round (reasoning, tool request, tool response).
const THINKING_ROLES = ['reasoning', 'tool_req', 'tool_rsp'];
const isThinkingRole = (role) => THINKING_ROLES.includes(role);

// buildItems groups the ordered message list into render items. Consecutive
// thinking messages (reasoning, tool_req, tool_rsp) are folded into a single
// "thinking" super bubble, bounded by any user/assistant/error/command message.
// Command messages always render as their own independent bubbles. The optional
// in-flight streaming message is appended to the current thinking group when it
// is a thinking role, or rendered as a standalone item otherwise.
function buildItems(messages, streaming) {
  const items = [];
  let think = null; // { msgs: [], streaming: null }

  const flush = () => {
    if (think) {
      items.push(think);
      think = null;
    }
  };

  for (const msg of messages) {
    if (isThinkingRole(msg.role)) {
      if (!think) think = { msgs: [], streaming: null };
      think.msgs.push(msg);
    } else {
      flush();
      items.push({ single: msg });
    }
  }

  if (streaming) {
    if (isThinkingRole(streaming.role)) {
      if (!think) think = { msgs: [], streaming: null };
      think.streaming = streaming;
    } else {
      flush();
      items.push({ singleStream: streaming });
    }
  }
  flush();

  return items.map((it) => {
    if (it.single) return { kind: 'single', msg: it.single };
    if (it.singleStream) return { kind: 'singleStream', streaming: it.singleStream };
    return { kind: 'thinking', msgs: it.msgs, streaming: it.streaming || null };
  });
}

export function SessionPage({ id, sessions }) {
  const sess = sessions.get(id);
  const { messages, streaming, pendingMsgs, hasMore, loadEarlier } = useSessionEvents(id);
  const scrollRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  // Record this session as the "open" one so a PWA share target can attach a
  // shared item to it. Cleared when the user leaves the session.
  useEffect(() => {
    setOpenSession(id);
    return () => setOpenSession(null);
  }, [id]);

  // Proactively show the typing placeholder as soon as a message is sent, and
  // keep it while the session is running. Reset once the session is idle.
  useEffect(() => {
    if (sess && sess.state === 'idle') setSending(false);
  }, [sess && sess.state]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming, sending]);

  // Track whether the user has scrolled away from the bottom so a "scroll to
  // bottom" button can be shown. The container is near the bottom when its
  // scroll position is within a small threshold of the bottom.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    setShowScrollBtn(!nearBottom);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const running = sess ? sess.state === 'running' : false;
  const showTyping = sending || running;

  // Read marking: mark the session read on the latest full event received,
  // irrespective of role. To avoid marking on every incremental event, only
  // mark once the turn settles — when the session goes idle, or 1 second after
  // the last event is received (the timer resets on each new event).
  const isWeb = sess?.channel_id?.startsWith('web:');
  const latestFullID = messages.length ? messages[messages.length - 1].id : null;
  const idleState = sess ? sess.state : null;
  useEffect(() => {
    if (!isWeb) return;
    const mark = () => {
      if (latestFullID) markRead(sess.id, latestFullID);
    };
    if (idleState === 'idle') {
      mark();
      return;
    }
    const t = setTimeout(mark, 1000);
    return () => clearTimeout(t);
  }, [isWeb, sess?.id, idleState, latestFullID]);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex items-center gap-4 px-4 py-3 border-b border-base-300 bg-base-100">
        {running && <span className="running-dot" />}
        {sess && sess.unread && !running && <span className="unread-dot" />}
        <h1 className="text-lg font-semibold text-base-content truncate">
          {sess ? sess.title : 'Session'}
        </h1>
      </div>

      <div className="relative flex-1 min-h-0">
        <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto px-4 py-4">
          <div className="w-full max-w-3xl mx-auto flex flex-col gap-3">
            {hasMore && messages.length > 0 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm self-center text-base-content/50"
                onClick={loadEarlier}
              >
                Load earlier messages
              </button>
            )}
            {(() => {
              const items = buildItems(messages, streaming);
              return items.map((item, i) => {
                if (item.kind === 'thinking') {
                  return <ThinkingBubble key={`think-${i}`} msgs={item.msgs} streaming={item.streaming} />;
                }
                if (item.kind === 'singleStream') {
                  return <StreamingBubble key={`stream-${i}`} role={item.streaming.role} text={item.streaming.text} />;
                }
                return <EventBubble key={item.msg.id} msg={item.msg} />;
              });
            })()}
            {showTyping && <TypingIndicator />}
            {pendingMsgs.map((msg) => (
              <PendingBubble key={msg.id} msg={msg} />
            ))}
            {messages.length === 0 && !streaming && !showTyping && (
              <p className="text-base-content/50 text-sm text-center py-8">No messages yet.</p>
            )}
          </div>
        </div>

        {showScrollBtn && (
          <button
            type="button"
            aria-label="Scroll to bottom"
            className="btn btn-circle btn-lg absolute bottom-6 right-6 shadow-lg z-10"
            onClick={scrollToBottom}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" class="w-8 h-8" aria-hidden="true">
              <path fill-rule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v10.638l3.96-4.158a.75.75 0 1 1 1.08 1.04l-5.25 5.5a.75.75 0 0 1-1.08 0l-5.25-5.5a.75.75 0 1 1 1.08-1.04l3.96 4.158V3.75A.75.75 0 0 1 10 3Z" clip-rule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      <div className="border-t border-base-300 bg-base-100 px-4 py-3">
        <Compose
          placeholder="Send a message..."
          onSubmit={(text, files) => {
            setSending(true);
            sendMessage(id, text, files || []);
          }}
        />
      </div>
    </div>
  );
}


// ClockIcon is a 20x20 clock glyph used for pending message bubbles.
function ClockIcon({ className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" class={className} aria-hidden="true">
      <path fill-rule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z"
        clip-rule="evenodd" />
    </svg>
  );
}

// PendingBubble renders a user message that arrived while the session was
// running. The bubble itself is styled by EventBubble per its role; a clock
// icon sits immediately below it with the same horizontal alignment (right
// for user/command_req, left for assistant/error/command_rsp). It sits below
// the typing indicator until the agent round ends and the real (non-pending)
// version replaces it.
function PendingBubble({ msg }) {
  const isRight = msg.role === 'user' || msg.role === 'command_req';
  return (
    <div className={`flex flex-col gap-0.5 ${isRight ? 'items-end' : 'items-start'}`}>
      <EventBubble msg={msg} />
      <ClockIcon className="w-3.5 h-3.5 text-base-content/40 shrink-0" />
    </div>
  );
}

// TypingIndicator shows a three-dot "thinking" placeholder while the agent is
// running (from message send until the session becomes idle).
function TypingIndicator() {
  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="inline-flex items-center gap-1 rounded-2xl bg-base-200 px-4 py-3">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-2 h-2 rounded-full bg-base-content/40 animate-bounce"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function StreamingBubble({ role, text }) {
  if (role === 'reasoning') {
    return (
      <div className="w-full max-w-3xl mx-auto event-round-thinking flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs text-base-content/40">
          <Icon name="lightbulb" className="w-3.5 h-3.5 shrink-0" />
          <span className="font-medium">Thinking</span>
        </div>
        <Markdown className="text-sm prose prose-sm max-w-none text-base-content/60" text={text} />
      </div>
    );
  }
  return (
    <div className="w-full max-w-3xl mx-auto">
      <Markdown className="event-content prose prose-sm max-w-none" text={text} />
    </div>
  );
}